import type { Copy } from "../types";

export const he: Copy = {
    seo: {
      title: "מחשבון גיל רכב | בדיקה לפי שנת וחודש ייצור",
      description:
        "בדקו את הגיל המדויק של רכב לפי שנת הייצור והחודש האופציונלי. צפו בשנים, חודשים, ימים, סך הימים ואבני דרך בכלי נקי אחד.",
      keywords: [
        "מחשבון גיל רכב",
        "גיל הרכב",
        "גיל רכב לפי שנה",
        "שנת ייצור רכב",
        "חודש ייצור רכב",
        "בן כמה הרכב שלי",
        "בדיקת גיל רכב",
        "מחשבון גיל RC",
        "מחשבון תאריך רכב",
        "מחשבון גיל רכב לפי חודש",
        "חלופה ל-mParivahan",
        "חלופה ל-Parivahan",
        "מחשבון גיל רכב RTO",
        "בדיקת גיל רישום רכב",
        "גיל רישום רכב",
      ],
      faq: [
        {
          question: "מה עושה המחשבון הזה?",
          answer:
            "הוא מחשב את הגיל המדויק של רכב לפי שנת הייצור שנבחרה והחודש האופציונלי, ואז מציג שנים, חודשים, ימים ומידע על אבני דרך.",
        },
        {
          question: "למה יש חודש אופציונלי?",
          answer:
            "בחירת חודש הופכת את החישוב למדויק יותר. אם מדלגים על החודש, האפליקציה משתמשת בינואר של השנה שנבחרה.",
        },
        {
          question: "אפשר לבחור שנה או חודש עתידיים?",
          answer:
            "לא. שנים עתידיות חסומות, וחודשים עתידיים מושבתים כאשר השנה שנבחרה היא השנה הנוכחית.",
        },
      ],
    },
    headerTagline: "כלי לרכב",
    headerTitle: "מחשבון גיל רכב",
    reset: "איפוס",
    switchToLight: "מצב בהיר",
    switchToDark: "מצב כהה",
    languageLabel: "שפה",
    languageEnglish: "אנגלית",
    languageHindi: "הינדי",
    heroEyebrow: "גיל הרכב, ברור",
    heroTitle: "כלי תאריך נקי לגיל הרכב.",
    heroDescription:
      "בחרו את שנת הייצור. הוסיפו חודש רק אם אתם רוצים תוצאה מדויקת יותר.",
    yearSelectLabel: "שנת ייצור",
    monthSelectLabel: "חודש ייצור",
    includeMonthLabel: "כלול חודש ספציפי ליותר דיוק",
    includeMonthNote: "אם לא תכללו חודש, ינואר ישמש לחישוב.",
    yearOnlyBadge: "שנה בלבד",
    yearMonthBadge: "שנה + חודש",
    yearPlaceholder: "בחרו שנה",
    monthPlaceholder: "בחרו חודש",
    inputTitle: "שנת ייצור",
    inputLabel: "שנת ייצור",
    blockedDateNote: "שנים עתידיות חסומות.",
    result: "תוצאה",
    vehicleAgeLead: "הרכב שלך",
    asOfToday: "נכון להיום",
    years: "שנים",
    months: "חודשים",
    days: "ימים",
    totalDays: (count) => `${count.toLocaleString("en-IN")} ימים בסך הכול`,
    ageLabels: { almostNew: "כמעט חדש", modern: "מודרני", mature: "בשל", classic: "קלאסי" },
    estimatedValue: {
      high: "פוטנציאל מכירה חוזרת גבוה",
      healthy: "שמירת ערך טובה",
      moderate: "ירידת ערך מתונה",
      collector: "עניין האספנים עשוי לגדול",
    },
    maintenanceTitle: "תחזוקה",
    maintenance: {
      routine: "טיפול שגרתי ותחזוקת OEM",
      watch: "עקבו אחר בלמים, נוזלים וצמיגים",
      plan: "תכננו חלקי שחיקה ובדיקות",
      specialized: "תחזוקה ייעודית נעשית חשובה יותר",
    },
    registrationTitle: "רישום",
    registration: {
      collector: "באזורים מסוימים עשוי לחול סיווג אספנות",
      classic: "באזורים מסוימים עשוי לחול רישום קלאסי",
      standard: "צפו לחידושים רגילים ובדיקות אזוריות",
      regular: "בדרך כלל חל רישום רגיל",
    },
    upcomingMilestoneTitle: "אבן הדרך הבאה",
    milestoneTimelineTitle: "ציר הזמן של אבני הדרך",
    milestoneRemaining: ({ label, days }) => `${label} בעוד ${days.toLocaleString("he-IL")} ימים`,
    reached: "הושג",
    upcoming: "קרוב",
    allMilestonesReachedTitle: "כל אבני הדרך ברשימה הושגו",
    allMilestonesReachedBody: "הרכב הזה עבר כבר את טווח אבני הדרך שמוצג כרגע באפליקציה.",
    copySummary: "העתקת סיכום",
    shareResult: "שיתוף תוצאה",
    resetMessage: "המחשבון אופס",
    summaryCopied: "הסיכום הועתק ללוח",
    clipboardFailed: "הגישה ללוח נכשלה",
    emptyTitle: "בחרו שנת ייצור כדי לראות את הפירוט המלא",
    emptyDescription:
      "לאחר בחירת השנה תראו גיל מדויק, סך ימים, הערות תחזוקה, מצב קלאסי ותזמון אבני הדרך.",
    shareTitle: "מחשבון גיל רכב",
    summary: ({ label, years, months, days, asOf }) =>
      `לרכב שלי תאריך ייצור ${label}, ונכון ל-${asOf} הוא בן ${years} שנים, ${months} חודשים ו-${days} ימים.`,
    milestones: {
      1: { label: "השנה הראשונה", note: "שימושי לתכנון אחריות ושירות." },
      3: { label: "שלוש שנים", note: "נקודת זמן נפוצה למכירה חוזרת ולשינויי ערך." },
      5: { label: "חמש שנים", note: "זמן טוב לתקציב תחזוקה עמוק יותר." },
      10: { label: "עשר שנים", note: "רכיבים ותיקים דורשים יותר תשומת לב." },
      15: { label: "חמש עשרה שנים", note: "באזורים רבים מתחיל כאן טיפול קלאסי." },
      25: { label: "עשרים וחמש שנים", note: "יכול לעניין אספנים ושווקי יבוא." },
    },
  };
